import React, { useState, useEffect } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import './Puzzle.css';

/* eslint-disable react-hooks/exhaustive-deps */

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru';

const PUZZLE_EXERCISES = [
  [
    { id: 'apple', rows: 2, cols: 2 },
    { id: 'pear', rows: 1, cols: 2 },
    { id: 'onion', rows: 2, cols: 1 },
  ],
  [
    { id: 'tiger', rows: 2, cols: 1 },
    { id: 'dog', rows: 2, cols: 2 },
    { id: 'owl', rows: 1, cols: 2 },
  ],
];

const shuffleArray = (arr) => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const buildEmptyPlacements = (puzzleSet) => {
  const placements = {};
  puzzleSet.forEach(puzzle => {
    placements[puzzle.id] = new Array(puzzle.rows * puzzle.cols).fill(null);
  });
  return placements;
};

const buildShuffledTray = (puzzleSet) => {
  let allPieces = [];
  puzzleSet.forEach(puzzle => {
    const total = puzzle.rows * puzzle.cols;
    for (let i = 0; i < total; i++) {
      allPieces.push({
        key: `${puzzle.id}-${i}`,
        puzzleId: puzzle.id,
        pieceIndex: i,
        url: `${S3_BASE_URL}/puzzles/${puzzle.id}_${i + 1}.png`,
      });
    }
  });
  return shuffleArray(allPieces);
};

const Puzzle = ({ onBack, onComplete }) => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [tray, setTray] = useState([]);
  const [placements, setPlacements] = useState({});
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [mistakeCounts, setMistakeCounts] = useState({});
  const [puzzleLog, setPuzzleLog] = useState([]);

  const currentExercisePuzzles = PUZZLE_EXERCISES[currentExerciseIndex];
  const totalExercises = PUZZLE_EXERCISES.length;

  useEffect(() => {
    setTray(buildShuffledTray(currentExercisePuzzles));
    setPlacements(buildEmptyPlacements(currentExercisePuzzles));
    setMistakeCounts({});
    setSelected(null);
  }, [currentExerciseIndex]);

  const registerMistakeIfWrong = (piece, destPuzzleId, destCellIndex) => {
    if (!piece) return;
    if (piece.puzzleId !== destPuzzleId || piece.pieceIndex !== destCellIndex) {
      setMistakeCounts(prev => ({ ...prev, [destPuzzleId]: (prev[destPuzzleId] || 0) + 1 }));
    }
  };

  const movePieceInto = (source, destPuzzleId, destCellIndex) => {
    if (!source) return;
    if (source.type === 'tray') {
      const piece = tray.find(p => p.key === source.key);
      if (!piece) return;
      const destPiece = placements[destPuzzleId][destCellIndex];
      registerMistakeIfWrong(piece, destPuzzleId, destCellIndex);
      setPlacements(prev => ({ ...prev, [destPuzzleId]: prev[destPuzzleId].map((slot, idx) => (idx === destCellIndex ? piece : slot)) }));
      setTray(prev => [...prev.filter(p => p.key !== piece.key), destPiece].filter(Boolean));
    } else {
      const { puzzleId: srcPuzzleId, cellIndex: srcCellIndex } = source;
      if (srcPuzzleId === destPuzzleId && srcCellIndex === destCellIndex) return;
      const srcPiece = placements[srcPuzzleId][srcCellIndex];
      const destPiece = placements[destPuzzleId][destCellIndex];
      registerMistakeIfWrong(srcPiece, destPuzzleId, destCellIndex);
      setPlacements(prev => {
        const updated = { ...prev };
        updated[srcPuzzleId] = [...prev[srcPuzzleId]];
        updated[destPuzzleId] = destPuzzleId === srcPuzzleId ? updated[srcPuzzleId] : [...prev[destPuzzleId]];
        updated[srcPuzzleId][srcCellIndex] = destPiece || null;
        updated[destPuzzleId][destCellIndex] = srcPiece;
        return updated;
      });
    }
  };

  const isSameSource = (a, b) => {
    if (!a || !b || a.type !== b.type) return false;
    if (a.type === 'tray') return a.key === b.key;
    return a.puzzleId === b.puzzleId && a.cellIndex === b.cellIndex;
  };

  const handleTrayPieceClick = (piece) => {
    const source = { type: 'tray', key: piece.key };
    if (isSameSource(selected, source)) { setSelected(null); return; }
    setSelected(source);
  };

  const handleCellClick = (puzzleId, cellIndex) => {
    const cellSource = { type: 'cell', puzzleId, cellIndex };
    if (!selected) {
      if (placements[puzzleId][cellIndex]) setSelected(cellSource);
      return;
    }
    if (isSameSource(selected, cellSource)) { setSelected(null); return; }
    movePieceInto(selected, puzzleId, cellIndex);
    setSelected(null);
  };

  const handleDragStartTray = (e, piece) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'tray', key: piece.key })); };
  const handleDragStartCell = (e, puzzleId, cellIndex) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', JSON.stringify({ type: 'cell', puzzleId, cellIndex })); };
  const handleDragOverCell = (e) => e.preventDefault();
  const handleDropOnCell = (e, puzzleId, cellIndex) => {
    e.preventDefault();
    try { movePieceInto(JSON.parse(e.dataTransfer.getData('text/plain')), puzzleId, cellIndex); } catch (err) {}
    setSelected(null);
  };

  const isPuzzleSolved = (puzzle) => {
    const arr = placements[puzzle.id];
    if (!arr) return false;
    return arr.every((slot, idx) => slot && slot.puzzleId === puzzle.id && slot.pieceIndex === idx);
  };

  const countCorrectPieces = (puzzle) => {
    const arr = placements[puzzle.id];
    if (!arr) return 0;
    return arr.filter((slot, idx) => slot && slot.puzzleId === puzzle.id && slot.pieceIndex === idx).length;
  };

  const isPuzzleFull = (puzzle) => {
    const arr = placements[puzzle.id];
    if (!arr) return false;
    return arr.every(slot => slot !== null);
  };

  useEffect(() => {
    const allFull = currentExercisePuzzles.length > 0 && currentExercisePuzzles.every(isPuzzleFull);
    if (allFull) {
      const timer = setTimeout(() => {
        setPuzzleLog(prev => [
          ...prev,
          ...currentExercisePuzzles.map(p => ({
            id: p.id,
            pieces: p.rows * p.cols,
            mistakeCount: mistakeCounts[p.id] || 0,
            isCorrect: isPuzzleSolved(p),
            correctPieces: countCorrectPieces(p),
          })),
        ]);

        if (currentExerciseIndex < totalExercises - 1) {
          setCurrentExerciseIndex(currentExerciseIndex + 1);
        } else {
          setShowResult(true);
        }
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [placements]);

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const totalPuzzles = puzzleLog.length;
        const correctCount = puzzleLog.filter(p => p.isCorrect).length;
        const score = totalPuzzles > 0 ? Math.round((correctCount / totalPuzzles) * 100) : 0;

        if (onComplete) {
          onComplete({
            testId: 'Puzzle',
            score,
            details: { total: totalPuzzles, correct: correctCount, puzzles: puzzleLog },
          });
        } else {
          onBack();
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (showResult) {
    return (
      <ModuleTemplate progress={[]} totalQuestions={1} forceStop={true} onComplete={onComplete || onBack}>
        <div className="result-content">
          <h2 className="result-title">🧩 Все пазлы собраны!</h2>
        </div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate progress={[]} totalQuestions={1} forceStop={true} onComplete={onComplete || onBack}>
      <div className="puzzle-container">

        <div className="puzzle-frames-row">
          {currentExercisePuzzles.map((puzzle) => {
            const solved = isPuzzleSolved(puzzle);

            if (solved) {
              return (
                <div key={puzzle.id} className="puzzle-frame puzzle-frame-solved">
                  <img src={`${S3_BASE_URL}/puzzles/${puzzle.id}.png`} alt="" className="puzzle-frame-solved-image" onError={(e) => { e.target.src = '/placeholder.png'; }} />
                </div>
              );
            }

            const cellWidth = 100 / puzzle.cols;
            const cellHeight = 100 / puzzle.rows;
            return (
              <div
                key={puzzle.id}
                className="puzzle-frame"
                style={{ gridTemplateColumns: `repeat(${puzzle.cols}, ${cellWidth}%)`, gridTemplateRows: `repeat(${puzzle.rows}, ${cellHeight}%)` }}
              >
                {placements[puzzle.id] && placements[puzzle.id].map((slot, cellIndex) => {
                  const cellSource = { type: 'cell', puzzleId: puzzle.id, cellIndex };
                  const isSelected = isSameSource(selected, cellSource);
                  return (
                    <div
                      key={cellIndex}
                      className={`puzzle-cell ${slot ? 'puzzle-cell-filled' : 'puzzle-cell-empty'} ${isSelected ? 'puzzle-cell-picked' : ''}`}
                      onClick={() => handleCellClick(puzzle.id, cellIndex)}
                      onDragOver={handleDragOverCell}
                      onDrop={(e) => handleDropOnCell(e, puzzle.id, cellIndex)}
                      draggable={!!slot}
                      onDragStart={(e) => slot && handleDragStartCell(e, puzzle.id, cellIndex)}
                    >
                      {slot && <img src={slot.url} alt="" className="puzzle-cell-image" onError={(e) => { e.target.src = '/placeholder.png'; }} />}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="puzzle-tray-row">
          {tray.map((piece) => {
            const pieceSource = { type: 'tray', key: piece.key };
            const isSelected = isSameSource(selected, pieceSource);
            return (
              <div
                key={piece.key}
                className={`puzzle-tray-item ${isSelected ? 'puzzle-tray-item-selected' : ''}`}
                onClick={() => handleTrayPieceClick(piece)}
                draggable
                onDragStart={(e) => handleDragStartTray(e, piece)}
              >
                <img src={piece.url} alt="" className="puzzle-tray-image" onError={(e) => { e.target.src = '/placeholder.png'; }} />
              </div>
            );
          })}
        </div>

      </div>
    </ModuleTemplate>
  );
};

export default Puzzle;